import { normalizeCountryCode } from '@rover/constants/src/geography/countryCode';
import { normalizeLocale } from '@rover/constants/src/i18n/locale';

import { RoverStatsigUser, StatsigUser } from './types';

const ENV_USER_ID_SEPARATOR = ':';

// Composite `environment:user` custom ID that scopes a Statsig user override to
// the single environment (codespace/staging) it was created in, mirroring the
// backend `env_user_id` (see aplaceforrover/systems/statsig/utils.py). Only set
// when both an environment_id and a userID are present — production has no
// environment_id, and an anonymous user has no id to bind an override to.
export const buildEnvUserId = (
  environmentId: string | undefined,
  userID: string | undefined
): string | undefined =>
  environmentId && userID ? `${environmentId}${ENV_USER_ID_SEPARATOR}${userID}` : undefined;

const customAttributesChanged = (
  currentCustom: StatsigUser['custom'] | undefined,
  newCustom: RoverStatsigUser['custom'] | undefined
): boolean => {
  const currentKeys = currentCustom ? Object.keys(currentCustom) : [];
  const newKeys = newCustom ? Object.keys(newCustom) : [];

  if (currentKeys.length !== newKeys.length) {
    return true;
  }

  if (!currentKeys.every((key) => newKeys.includes(key))) {
    return true;
  }

  for (const key of newKeys) {
    if (currentCustom?.[key] !== newCustom?.[key]) {
      return true;
    }
  }

  return false;
};

export const userNeedsUpdate = (
  currentUser: StatsigUser | undefined,
  newUser: RoverStatsigUser
): boolean => {
  if (!currentUser) {
    return true;
  }

  if ((currentUser.appVersion ?? null) !== (newUser.appVersion ?? null)) return true;
  if (currentUser.country !== newUser.country) return true;
  if (currentUser.email !== newUser.email) return true;
  if (currentUser.userID !== newUser.userID) return true;
  if (currentUser.customIDs?.rover_cid !== newUser.customIDs?.rover_cid) return true;
  if (currentUser.customIDs?.environment_id !== newUser.customIDs?.environment_id) return true;
  if (currentUser.customIDs?.env_user_id !== newUser.customIDs?.env_user_id) return true;
  if (customAttributesChanged(currentUser.custom, newUser.custom)) return true;

  return false;
};

export const mapExperimentUserAttributesToRoverStatsigUser = (
  experimentUserAttributes?: Record<string, any>
): RoverStatsigUser => {
  if (!experimentUserAttributes) return {};
  const userID = experimentUserAttributes.userId ?? undefined;
  // Handle both camelCase (from API) and snake_case (from context with blocklist) as input
  const environmentId =
    experimentUserAttributes.customIds?.environmentId ??
    experimentUserAttributes.customIds?.environment_id;
  return {
    userID,
    email: experimentUserAttributes.email ?? undefined,
    country: normalizeCountryCode(experimentUserAttributes.country),
    ip: experimentUserAttributes.ip ?? undefined,
    userAgent: experimentUserAttributes.userAgent ?? undefined,
    appVersion: experimentUserAttributes.appVersion ?? undefined,
    locale: normalizeLocale(experimentUserAttributes.locale),
    custom: {
      ...experimentUserAttributes.custom,
    },
    customIDs: {
      // Output snake_case keys to match Statsig console configuration
      rover_cid:
        experimentUserAttributes.customIds?.roverCid ??
        experimentUserAttributes.customIds?.rover_cid,
      environment_id: environmentId,
      env_user_id: buildEnvUserId(environmentId, userID),
    },
  };
};

export const createUserPayload = (
  newUser?: RoverStatsigUser,
  currentUser?: StatsigUser
): Partial<StatsigUser> => {
  const payload: Partial<StatsigUser> = {};
  payload.appVersion = newUser?.appVersion ?? currentUser?.appVersion;
  payload.country = newUser?.country ?? currentUser?.country;
  payload.email = newUser?.email;
  payload.userID = newUser?.userID;
  payload.custom = {
    ...currentUser?.custom,
    ...newUser?.custom,
  };
  const environmentId =
    newUser?.customIDs?.environment_id ?? currentUser?.customIDs?.environment_id;
  payload.customIDs = {
    rover_cid: newUser?.customIDs?.rover_cid ?? currentUser?.customIDs?.rover_cid,
    environment_id: environmentId,
    // Derived from the resolved id pair so it never carries a stale value when
    // userID changes (e.g. login/logout).
    env_user_id: buildEnvUserId(environmentId, payload.userID),
  };

  return payload;
};
